import React, { useEffect, useMemo, useState } from 'react';
import { Button, Section, SectionHeader } from '../../design-system';
import MarketingLayout from '../layouts/MarketingLayout';
import FadeIn from '../components/FadeIn';
import LiveNetworkSection from '../components/LiveNetworkSection';
import ProofStatsBar from '../components/ProofStatsBar';
import ProofPhotoWall from '../components/ProofPhotoWall';
import { useNetworkStatus } from '../hooks/useNetworkStatus';
import { useNetworkStats } from '../hooks/useNetworkStats';
import { getNetworkScreens } from '../../services/getNetworkScreens';
import {
  ADVERTISER_LOGOS,
  FOUNDER_STORY,
  getFilledBeforeAfter,
  getFilledCampaignExamples,
  getFilledDashboardScreenshots,
  getFilledTestimonials,
  getFilledVideos,
  hasFounderStory,
} from '../data/proof';
import SeoHead from '../seo/SeoHead';
import { PROOF_SEO } from '../seo/pages';

const normalize = (value) => (value || '').toLowerCase().replace(/[^a-z0-9]/g, '');

function findAdvertiserLogo(name) {
  const target = normalize(name);
  if (!target) return null;
  return (
    ADVERTISER_LOGOS.find((a) => {
      if (!a.logo) return false;
      const label = normalize(a.label);
      const full = normalize(a.name);
      return (
        label === target ||
        full === target ||
        (label && (target.includes(label) || label.includes(target))) ||
        (full && full.includes(target))
      );
    }) || null
  );
}

function PartnerCard({ name, photoUrl, location }) {
  return (
    <div className="rounded-2xl border border-invert-border overflow-hidden bg-white hover:shadow-md transition-shadow">
      {photoUrl ? (
        <div className="aspect-[4/3] bg-slate-100">
          <img src={photoUrl} alt={name} className="w-full h-full object-cover" loading="lazy" />
        </div>
      ) : (
        <div className="aspect-[4/3] bg-slate-100 flex items-center justify-center">
          <span className="text-4xl" aria-hidden="true">📍</span>
        </div>
      )}
      <div className="p-4">
        <h3 className="font-semibold text-invert-navy">{name}</h3>
        {location && <p className="text-sm text-slate-500 mt-1">{location}</p>}
      </div>
    </div>
  );
}

function NameChip({ name }) {
  const advertiser = findAdvertiserLogo(name);

  if (advertiser?.logo) {
    return (
      <span className="inline-flex items-center justify-center h-16 px-6 rounded-2xl border border-invert-border bg-white">
        <img
          src={advertiser.logo}
          alt={advertiser.label || name}
          loading="lazy"
          className="max-h-9 w-auto object-contain"
        />
      </span>
    );
  }

  return (
    <span className="inline-flex items-center px-4 py-2 rounded-full border border-invert-border bg-white text-sm font-medium text-invert-navy">
      {name}
    </span>
  );
}

function TestimonialCard({ testimonial }) {
  return (
    <div className="rounded-2xl border border-invert-border p-6 bg-white h-full">
      <p className="text-slate-700 leading-relaxed mb-4">&ldquo;{testimonial.quote}&rdquo;</p>
      <div>
        <p className="font-semibold text-invert-navy">{testimonial.name}</p>
        <p className="text-sm text-slate-500">
          {[testimonial.role, testimonial.venue].filter(Boolean).join(' · ')}
        </p>
      </div>
    </div>
  );
}

export default function Proof() {
  const { screens, liveScreens, loading: statusLoading } = useNetworkStatus();
  const { stats } = useNetworkStats();
  const [galleryScreens, setGalleryScreens] = useState([]);

  useEffect(() => {
    getNetworkScreens()
      .then((data) => setGalleryScreens(Array.isArray(data.screens) ? data.screens : []))
      .catch(() => setGalleryScreens([]));
  }, []);

  const venuePartners = useMemo(() => {
    const byBrand = new Map();
    screens.forEach((screen) => {
      const name = screen.brand;
      if (!name || name.startsWith('Screen #')) return;
      if (!byBrand.has(name)) {
        byBrand.set(name, {
          name,
          photoUrl: screen.listingPhotoUrl || null,
          location: screen.locationDescription || null,
        });
      } else if (!byBrand.get(name).photoUrl && screen.listingPhotoUrl) {
        byBrand.get(name).photoUrl = screen.listingPhotoUrl;
      }
    });
    return [...byBrand.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [screens]);

  const recentInstallations = useMemo(() => {
    return [...screens]
      .filter((s) => s.installedDate)
      .sort((a, b) => new Date(b.installedDate) - new Date(a.installedDate))
      .slice(0, 6);
  }, [screens]);

  const testimonials = getFilledTestimonials();
  const campaignExamples = getFilledCampaignExamples();
  const beforeAfter = getFilledBeforeAfter();
  const videos = getFilledVideos();
  const dashboardShots = getFilledDashboardScreenshots();
  const showFounder = hasFounderStory();

  const advertiserNames = stats?.advertisers?.length
    ? stats.advertisers
    : [];

  return (
    <MarketingLayout>
      <SeoHead title={PROOF_SEO.title} description={PROOF_SEO.description} path={PROOF_SEO.path} />
      {/* Hero */}
      <section className="bg-invert-navy text-white pt-32 lg:pt-40 pb-20 lg:pb-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <FadeIn>
            <p className="text-xs font-semibold uppercase tracking-widest text-invert-orange mb-6">
              Proof
            </p>
            <h1 className="font-interblack text-4xl sm:text-5xl lg:text-6xl tracking-tight mb-6 max-w-4xl mx-auto">
              Real restaurant screens. Real results.
            </h1>
            <p className="text-xl text-slate-300 max-w-2xl mx-auto mb-8">
              Restaurants and venues already running INVERT restaurant TV content across Los Angeles.
            </p>
            {!statusLoading && liveScreens > 0 && (
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20 text-sm font-medium">
                <span className="text-emerald-400">🟢</span>
                {liveScreens} screens live right now
              </div>
            )}
          </FadeIn>
        </div>
      </section>

      {/* Stats bar */}
      <Section className="!py-12 lg:!py-16">
        <ProofStatsBar />
      </Section>

      {/* Live network / gallery fallback */}
      <Section className="bg-slate-50">
        <LiveNetworkSection showStatusLink={false} />
      </Section>

      {/* Venue partners */}
      {venuePartners.length > 0 && (
        <Section>
          <SectionHeader
            label="Venue Partners"
            title="Every restaurant partner"
            subtitle="Real local businesses hosting INVERT screens in their venues."
          />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {venuePartners.map((partner, i) => (
              <FadeIn key={partner.name} delay={i * 0.06}>
                <PartnerCard {...partner} />
              </FadeIn>
            ))}
          </div>
        </Section>
      )}

      {/* Advertisers */}
      {advertiserNames.length > 0 && (
        <Section className="bg-slate-50">
          <SectionHeader
            label="Advertisers"
            title="Brands on the network"
            subtitle="Local and regional advertisers running campaigns on INVERT screens."
          />
          <div className="flex flex-wrap gap-3 justify-center">
            {advertiserNames.map((name) => (
              <NameChip key={name} name={name} />
            ))}
          </div>
        </Section>
      )}

      {/* Photo wall */}
      <Section>
        <SectionHeader
          label="Installations"
          title="Photos of every location"
          subtitle="Real venue photos — not renders. Every screen is installed in a live business."
        />
        <ProofPhotoWall apiScreens={galleryScreens.length ? galleryScreens : screens} />
      </Section>

      {/* Recent installations */}
      {recentInstallations.length > 0 && (
        <Section className="bg-slate-50">
          <SectionHeader
            label="Recent"
            title="Recent installations"
            subtitle="The newest screens joining the INVERT network."
          />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {recentInstallations.map((screen, i) => (
              <FadeIn key={screen.screenId} delay={i * 0.06}>
                <div className="rounded-2xl border border-invert-border bg-white overflow-hidden">
                  {screen.listingPhotoUrl ? (
                    <div className="aspect-[4/3] bg-slate-100">
                      <img
                        src={screen.listingPhotoUrl}
                        alt={screen.brand}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </div>
                  ) : (
                    <div className="aspect-[4/3] bg-slate-100 flex items-center justify-center text-4xl">
                      📺
                    </div>
                  )}
                  <div className="p-4">
                    <h3 className="font-semibold text-invert-navy">{screen.brand}</h3>
                    {screen.locationDescription && (
                      <p className="text-sm text-slate-500 mt-1">{screen.locationDescription}</p>
                    )}
                    <p className="text-xs text-slate-400 mt-2">
                      Installed {new Date(screen.installedDate).toLocaleDateString('en-US', {
                        month: 'short',
                        year: 'numeric',
                      })}
                    </p>
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
        </Section>
      )}

      {/* Campaign examples */}
      {campaignExamples.length > 0 && (
        <Section>
          <SectionHeader label="Campaigns" title="Campaign examples" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {campaignExamples.map((campaign, i) => (
              <FadeIn key={campaign.title} delay={i * 0.08}>
                <div className="rounded-2xl border border-invert-border overflow-hidden bg-white">
                  <div className="aspect-video bg-slate-100">
                    <img
                      src={campaign.image}
                      alt={campaign.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-4">
                    <h3 className="font-semibold text-invert-navy">{campaign.title}</h3>
                    <p className="text-sm text-slate-500">{campaign.advertiser}</p>
                    {campaign.metric && (
                      <p className="text-xs text-invert-orange font-medium mt-2">{campaign.metric}</p>
                    )}
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
        </Section>
      )}

      {/* Before/after */}
      {beforeAfter.length > 0 && (
        <Section className="bg-slate-50">
          <SectionHeader label="Transformations" title="Before & after" />
          <div className="grid md:grid-cols-2 gap-8">
            {beforeAfter.map((pair, i) => (
              <FadeIn key={pair.caption || i} delay={i * 0.1}>
                <div className="grid grid-cols-2 gap-3">
                  <figure>
                    <img src={pair.before} alt="Before" className="rounded-xl w-full aspect-[4/3] object-cover" />
                    <figcaption className="text-xs text-slate-500 mt-2 text-center">Before</figcaption>
                  </figure>
                  <figure>
                    <img src={pair.after} alt="After" className="rounded-xl w-full aspect-[4/3] object-cover" />
                    <figcaption className="text-xs text-slate-500 mt-2 text-center">After</figcaption>
                  </figure>
                </div>
                {pair.caption && (
                  <p className="text-sm text-slate-600 text-center mt-3">{pair.caption}</p>
                )}
              </FadeIn>
            ))}
          </div>
        </Section>
      )}

      {/* Videos */}
      {videos.length > 0 && (
        <Section>
          <SectionHeader label="Video" title="Screens in action" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {videos.map((video, i) => (
              <FadeIn key={video.caption || i} delay={i * 0.08}>
                <video
                  src={video.src}
                  poster={video.poster || undefined}
                  controls
                  className="w-full rounded-xl aspect-video bg-slate-900"
                >
                  <track kind="captions" />
                </video>
                {video.caption && (
                  <p className="text-sm text-slate-600 mt-2">{video.caption}</p>
                )}
              </FadeIn>
            ))}
          </div>
        </Section>
      )}

      {/* Testimonials */}
      {testimonials.length > 0 && (
        <Section className="bg-slate-50">
          <SectionHeader label="Testimonials" title="What partners say" />
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <FadeIn key={t.name} delay={i * 0.08}>
                <TestimonialCard testimonial={t} />
              </FadeIn>
            ))}
          </div>
        </Section>
      )}

      {/* Dashboard screenshots */}
      {dashboardShots.length > 0 && (
        <Section>
          <SectionHeader
            label="Platform"
            title="Dashboard & campaign proof"
            subtitle="Verified measurement and proof-of-play reporting built into every campaign."
          />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {dashboardShots.map((shot, i) => (
              <FadeIn key={shot.caption || i} delay={i * 0.08}>
                <figure className="rounded-2xl border border-invert-border overflow-hidden bg-white">
                  <img src={shot.src} alt={shot.alt || shot.caption} className="w-full" />
                  {shot.caption && (
                    <figcaption className="p-3 text-sm text-slate-600">{shot.caption}</figcaption>
                  )}
                </figure>
              </FadeIn>
            ))}
          </div>
        </Section>
      )}

      {/* Founder story */}
      {showFounder && (
        <Section dark>
          <div className="max-w-3xl mx-auto text-center">
            <FadeIn>
              {FOUNDER_STORY.photo && (
                <img
                  src={FOUNDER_STORY.photo}
                  alt={FOUNDER_STORY.name || 'Founder'}
                  className="w-24 h-24 rounded-full mx-auto mb-6 object-cover"
                />
              )}
              <p className="text-xs font-semibold uppercase tracking-widest text-invert-orange mb-4">
                Founder
              </p>
              {FOUNDER_STORY.name && (
                <h2 className="text-2xl lg:text-3xl font-bold text-white mb-4">{FOUNDER_STORY.name}</h2>
              )}
              <h3 className="text-xl text-slate-300 mb-6">{FOUNDER_STORY.headline}</h3>
              <div className="space-y-4 text-slate-400 leading-relaxed text-left">
                {FOUNDER_STORY.paragraphs.filter(Boolean).map((p) => (
                  <p key={p.slice(0, 40)}>{p}</p>
                ))}
              </div>
            </FadeIn>
          </div>
        </Section>
      )}

      {/* Monthly stats detail */}
      {stats && (
        <Section className="bg-slate-50">
          <SectionHeader
            label="Network Statistics"
            title="Monthly network statistics"
            subtitle="Real numbers from the INVERT advertising network."
          />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { label: 'Billable screens', value: stats.totalBillableScreens },
              { label: 'Live now', value: stats.liveScreens },
              { label: 'Ad plays today', value: stats.slidesPlayedToday?.toLocaleString('en-US') },
              { label: 'Ad plays this month', value: stats.slidesPlayedThisMonth?.toLocaleString('en-US') },
              { label: 'Active campaigns', value: stats.activeCampaigns },
              { label: 'Active advertisers', value: stats.activeAdvertisers },
              { label: 'Guaranteed plays / screen / month', value: stats.guaranteedPlaysPerScreenMonth?.toLocaleString('en-US') },
              { label: 'Campaigns from', value: `$${stats.pricingFromUsd}/mo` },
            ].map((item) => (
              <div key={item.label} className="rounded-2xl border border-invert-border bg-white p-5 text-center">
                <p className="text-2xl font-bold text-invert-navy mb-1">{item.value ?? '—'}</p>
                <p className="text-sm text-slate-500">{item.label}</p>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* CTA */}
      <Section dark className="text-center">
        <FadeIn className="max-w-xl mx-auto">
          <h2 className="text-2xl lg:text-3xl font-bold text-white mb-4">
            Join the network
          </h2>
          <p className="text-slate-400 mb-8">
            Host a screen in your venue or advertise to customers where they already are.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button to="/advertiserSignup" size="lg">
              Start Advertising
            </Button>
            <Button to="/screenHostSignup" variant="secondary" size="lg">
              Host a Screen
            </Button>
          </div>
        </FadeIn>
      </Section>
    </MarketingLayout>
  );
}
